import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FirebaseService } from '../services/firebaseService';

// Styled Components
const ProductDetailContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  background: #ffffff;
  min-height: 100vh;
`;

const BackIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 8px;
  vertical-align: middle;
`;

const BackButton = styled(motion.button)`
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    background: #b91c1c;
    transform: translateY(-2px);
  }
`;

const HeaderSection = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const ProductImage = styled.img`
  width: 100%;
  max-width: 600px;
  height: 500px;
  object-fit: contain;
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
`;

const ProviderLogo = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  position: absolute;
  top: 2rem;
  right: 2rem;
  border-radius: 10px;
  background: white;
  padding: 0.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    position: static;
    width: 60px;
    height: 60px;
    margin-top: 1rem;
  }
`;

const ProductTitleSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 3rem;
`;

const ProductTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  color: #1f2937;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ProductSubtitle = styled.p`
  font-size: 1.2rem;
  color: #6b7280;
  margin: 0;
  text-align: center;
  font-weight: 500;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const SpecificationsSection = styled.div`
  background: #f8fafc;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SectionIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const SpecItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const SpecLabel = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const SpecValue = styled.span`
  font-size: 1.1rem;
  color: #1f2937;
  font-weight: 600;
`;

const DimensionsSection = styled.div`
  background: #f8fafc;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
`;

const DimensionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const DimensionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const DimensionLabel = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const DimensionValue = styled.span`
  font-size: 1.2rem;
  color: #1f2937;
  font-weight: 700;
`;

const TechnicalSpecsSection = styled.div`
  background: #f8fafc;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
`;

const TechnicalSpecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const TechnicalSpecItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const TechnicalSpecLabel = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const TechnicalSpecValue = styled.span`
  font-size: 1.2rem;
  color: #1f2937;
  font-weight: 700;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
`;

const LoadingText = styled.p`
  font-size: 1.2rem;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  text-align: center;
`;

const ErrorText = styled.p`
  font-size: 1.2rem;
  color: #dc2626;
  margin: 0;
`;

// Interface for Groupes Electrogènes Product
interface GroupesElectrogenesProduct {
  id: string;
  name: string;
  mainImage: string;
  provider: string;
  description: string[];
  technicalSpecs: { [key: string]: string };
  additionalImages: string[];
  category: string;
  createdAt?: any;
  updatedAt?: any;
  // Groupes Electrogènes specific fields
  moteur?: string;
  alternateur?: string;
  capotage?: string;
  coffretDeCommande?: string;
  type?: string;
  capaciteReservoir?: string;
  autonomie75Charge?: string;
  puissancePRP?: string;
  puissanceLTP?: string;
  amperage?: string;
  regime?: string;
  frequence?: string;
  tension?: string;
  facteurPuissance?: string;
  charge25?: string;
  charge50?: string;
  charge75?: string;
  charge100?: string;
  prpPrimeRunningPower?: string;
  ltpLimitedTimePower?: string;
  poids?: string;
  longueur?: string;
  largeur?: string;
  hauteur?: string;
}

const GroupesElectrogenesDetail: React.FC = () => {
  const { id: productId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<GroupesElectrogenesProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) {
        setError('Product ID not provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const productData = await FirebaseService.getProduct(productId);
        if (productData) {
          setProduct(productData as GroupesElectrogenesProduct);
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  if (loading) {
    return (
      <ProductDetailContainer>
        <LoadingContainer>
          <LoadingText>Loading product details...</LoadingText>
        </LoadingContainer>
      </ProductDetailContainer>
    );
  }

  if (error || !product) {
    return (
      <ProductDetailContainer>
        <BackButton
          onClick={() => navigate(-1)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <BackIcon src="/images/groupesdetail/back.svg" alt="Back" /> Back
        </BackButton>
        <ErrorContainer>
          <ErrorText>{error || 'Product not found'}</ErrorText>
        </ErrorContainer>
      </ProductDetailContainer>
    );
  }

  return (
    <ProductDetailContainer>
      <BackButton
        onClick={() => navigate(-1)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <BackIcon src="/images/groupesdetail/back.svg" alt="Back" /> Back
      </BackButton>

      <HeaderSection>
        <ProductImage
          src={product.mainImage || '/images/logo.png'}
          alt={product.name || 'Product Image'}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = '/images/logo.png';
          }}
        />
        <ProviderLogo
          src={"/images/partners/" + (product.provider || "deutz") + ".png"}
          alt={(product.provider || "DEUTZ") + " Logo"}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = '/images/logo.png';
          }}
        />
      </HeaderSection>

      <ProductTitleSection>
        <ProductTitle>
          {product.name || 'Groupes Electrogènes'}
        </ProductTitle>
        <ProductSubtitle>
          {product.description && product.description.length > 0
            ? product.description.join(' ')
            : 'No description available'
          }
        </ProductSubtitle>
      </ProductTitleSection>

      <MainContent>
        <SpecificationsSection>
          <SectionTitle>
            <SectionIcon src="/images/groupesdetail/specification.svg" alt="Specifications" />
            Specifications
          </SectionTitle>
          <SpecsGrid>
            {product.moteur && (
              <SpecItem>
                <SpecLabel>Moteur</SpecLabel>
                <SpecValue>{product.moteur}</SpecValue>
              </SpecItem>
            )}
            {product.alternateur && (
              <SpecItem>
                <SpecLabel>Alternateur</SpecLabel>
                <SpecValue>{product.alternateur}</SpecValue>
              </SpecItem>
            )}
            {product.capotage && (
              <SpecItem>
                <SpecLabel>Capotage</SpecLabel>
                <SpecValue>{product.capotage}</SpecValue>
              </SpecItem>
            )}
            {product.coffretDeCommande && (
              <SpecItem>
                <SpecLabel>Coffret de Commande</SpecLabel>
                <SpecValue>{product.coffretDeCommande}</SpecValue>
              </SpecItem>
            )}
            {product.capaciteReservoir && (
              <SpecItem>
                <SpecLabel>Capacité Réservoir</SpecLabel>
                <SpecValue>{product.capaciteReservoir}</SpecValue>
              </SpecItem>
            )}
            {product.autonomie75Charge && (
              <SpecItem>
                <SpecLabel>Autonomie 75% Charge</SpecLabel>
                <SpecValue>{product.autonomie75Charge}</SpecValue>
              </SpecItem>
            )}
          </SpecsGrid>
        </SpecificationsSection>

        <DimensionsSection>
          <SectionTitle>
            <SectionIcon src="/images/groupesdetail/dimension.svg" alt="Dimensions" />
            Dimensions
          </SectionTitle>
          <DimensionsGrid>
            {product.longueur && (
              <DimensionItem>
                <DimensionLabel>Longueur</DimensionLabel>
                <DimensionValue>{product.longueur}</DimensionValue>
              </DimensionItem>
            )}
            {product.largeur && (
              <DimensionItem>
                <DimensionLabel>Largeur</DimensionLabel>
                <DimensionValue>{product.largeur}</DimensionValue>
              </DimensionItem>
            )}
            {product.hauteur && (
              <DimensionItem>
                <DimensionLabel>Hauteur</DimensionLabel>
                <DimensionValue>{product.hauteur}</DimensionValue>
              </DimensionItem>
            )}
            {product.poids && (
              <DimensionItem>
                <DimensionLabel>Poids</DimensionLabel>
                <DimensionValue>{product.poids}</DimensionValue>
              </DimensionItem>
            )}
          </DimensionsGrid>
        </DimensionsSection>

        <TechnicalSpecsSection>
          <SectionTitle>
            <SectionIcon src="/images/groupesdetail/technical.svg" alt="Technical Specifications" />
            Technical Specifications
          </SectionTitle>
          <TechnicalSpecsGrid>
            {product.puissancePRP && (
              <TechnicalSpecItem>
                <TechnicalSpecLabel>Puissance PRP</TechnicalSpecLabel>
                <TechnicalSpecValue>{product.puissancePRP}</TechnicalSpecValue>
              </TechnicalSpecItem>
            )}
            {product.puissanceLTP && (
              <TechnicalSpecItem>
                <TechnicalSpecLabel>Puissance LTP</TechnicalSpecLabel>
                <TechnicalSpecValue>{product.puissanceLTP}</TechnicalSpecValue>
              </TechnicalSpecItem>
            )}
            {product.amperage && (
              <TechnicalSpecItem>
                <TechnicalSpecLabel>Ampérage</TechnicalSpecLabel>
                <TechnicalSpecValue>{product.amperage}</TechnicalSpecValue>
              </TechnicalSpecItem>
            )}
            {product.regime && (
              <TechnicalSpecItem>
                <TechnicalSpecLabel>Régime</TechnicalSpecLabel>
                <TechnicalSpecValue>{product.regime}</TechnicalSpecValue>
              </TechnicalSpecItem>
            )}
            {product.frequence && (
              <TechnicalSpecItem>
                <TechnicalSpecLabel>Fréquence</TechnicalSpecLabel>
                <TechnicalSpecValue>{product.frequence}</TechnicalSpecValue>
              </TechnicalSpecItem>
            )}
            {product.tension && (
              <TechnicalSpecItem>
                <TechnicalSpecLabel>Tension</TechnicalSpecLabel>
                <TechnicalSpecValue>{product.tension}</TechnicalSpecValue>
              </TechnicalSpecItem>
            )}
            {product.facteurPuissance && (
              <TechnicalSpecItem>
                <TechnicalSpecLabel>Facteur de Puissance</TechnicalSpecLabel>
                <TechnicalSpecValue>{product.facteurPuissance}</TechnicalSpecValue>
              </TechnicalSpecItem>
            )}
            {product.charge25 && (
              <TechnicalSpecItem>
                <TechnicalSpecLabel>25% de charge</TechnicalSpecLabel>
                <TechnicalSpecValue>{product.charge25}</TechnicalSpecValue>
              </TechnicalSpecItem>
            )}
            {product.charge50 && (
              <TechnicalSpecItem>
                <TechnicalSpecLabel>50% de charge</TechnicalSpecLabel>
                <TechnicalSpecValue>{product.charge50}</TechnicalSpecValue>
              </TechnicalSpecItem>
            )}
            {product.charge75 && (
              <TechnicalSpecItem>
                <TechnicalSpecLabel>75% de charge</TechnicalSpecLabel>
                <TechnicalSpecValue>{product.charge75}</TechnicalSpecValue>
              </TechnicalSpecItem>
            )}
            {product.charge100 && (
              <TechnicalSpecItem>
                <TechnicalSpecLabel>100% de charge</TechnicalSpecLabel>
                <TechnicalSpecValue>{product.charge100}</TechnicalSpecValue>
              </TechnicalSpecItem>
            )}
          </TechnicalSpecsGrid>
        </TechnicalSpecsSection>
      </MainContent>
    </ProductDetailContainer>
  );
};

export default GroupesElectrogenesDetail;
